// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Monitors
//
// A "monitor" is either:
//   company — a monitoring firm (e.g. TetraTech) assigned to a site
//   person  — an individual monitor who works for a monitoring firm
//
// Both live in the global directory:
//   company  → Organizations (type: monitoring-company)
//   person   → People Directory (type: liaison), linked to their firm
//              via people.primaryOrgId → organizations.record_id
//
// Each incident engagement is an incident-scoped debrisMonitors record
// that links to the directory entry via orgId or personId.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, TextInput,
  Select, Modal, ActionIcon, Textarea, SimpleGrid,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconShieldCheck, IconBuilding, IconUser, IconX, IconLink,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { queryRecords, fetchRecords, createRecord, updateRecord, deleteRecord, fetchRecord } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { formatPhone } from '../../lib/phoneFormat';

const FM = FIELDS.debrisMonitors;
const FO = FIELDS.organizations;
const FP = FIELDS.people;

// ── Types ─────────────────────────────────────────────────────────

type MonitorType = 'person' | 'company';

interface Monitor {
  id: number;
  incidentId: string;
  monitorType: MonitorType;
  orgId: string;       // org FK (company-type: the firm; person-type: their employer)
  personId: string;    // people FK (person-type only)
  // Denormalized display fields (written at save time for backwards compat)
  fullName: string;
  company: string;
  email: string;
  phone: string;
  role: string;
  assignedSites: string;
  assignedZones: string;
  certifications: string;
  status: string;
  notes: string;
}

interface OrgEntry {
  record_id: number;
  name: string;
  type: string;
  phone: string;
  email: string;
  city: string;
  state: string;
  licenseNumber: string;
}

interface PersonEntry {
  record_id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  title: string;
  orgAffiliation: string;
  primaryOrgId: string;
}

const ROLE_OPTIONS = [
  { value: 'site_monitor',    label: 'Site Monitor'    },
  { value: 'field_verifier',  label: 'Field Verifier'  },
  { value: 'lead_monitor',    label: 'Lead Monitor'    },
  { value: 'qc_inspector',    label: 'QC Inspector'    },
];

const STATUS_OPTIONS = [
  { value: 'active',   label: 'Active'   },
  { value: 'standby',  label: 'Standby'  },
  { value: 'inactive', label: 'Inactive' },
];

const STATUS_COLOR: Record<string, string> = { active: 'var(--color-success)', standby: '#C68700', inactive: '#939598' };

function parseMonitor(r: Record<string, unknown>): Monitor {
  return {
    id:           r.record_id as number,
    incidentId:   FM.incidentId  ? String(r[FM.incidentId]  ?? '') : '',
    monitorType:  FM.monitorType ? (String(r[FM.monitorType] ?? 'person') as MonitorType) : 'person',
    orgId:        FM.orgId       ? String(r[FM.orgId]        ?? '') : '',
    personId:     FM.personId    ? String(r[FM.personId]     ?? '') : '',
    fullName:     String(r[FM.fullName]       ?? ''),
    company:      String(r[FM.company]        ?? ''),
    email:        String(r[FM.email]          ?? ''),
    phone:        String(r[FM.phone]          ?? ''),
    role:         String(r[FM.role]           ?? 'site_monitor'),
    assignedSites:String(r[FM.assignedSites]  ?? ''),
    assignedZones:String(r[FM.assignedZones]  ?? ''),
    certifications:String(r[FM.certifications]?? ''),
    status:       String(r[FM.status]         ?? 'active'),
    notes:        String(r[FM.notes]          ?? ''),
  };
}

// ── Directory search: Organizations ──────────────────────────────

function OrgSearch({ onSelect }: { onSelect: (o: OrgEntry) => void }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState<OrgEntry[]>([]);
  const [busy, setBusy]       = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = (q: string) => {
    setQuery(q);
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await queryRecords<Record<string, unknown>>(TABLES.organizations, {
          filters: [
            { field: FO.type, operator: 'in', value: 'monitoring-company,contractor,partner,ngo,business,other' },
            { field: FO.status, operator: 'eq', value: 'active' },
          ],
          sort: { field: FO.name, direction: 'asc' },
          limit: 20,
        });
        const lower = q.toLowerCase();
        setResults(res.records
          .map(r => ({
            record_id:     r.record_id as number,
            name:          String(r[FO.name]          ?? ''),
            type:          String(r[FO.type]          ?? ''),
            phone:         String(r[FO.phone]         ?? ''),
            email:         String(r[FO.email]         ?? ''),
            city:          String(r[FO.city]          ?? ''),
            state:         String(r[FO.state]         ?? ''),
            licenseNumber: String(r[FO.licenseNumber] ?? ''),
          }))
          .filter(o => o.name.toLowerCase().includes(lower))
        );
      } catch { /* ignore */ }
      setBusy(false);
    }, 280);
  };

  return (
    <div style={{ position: 'relative' }}>
      <TextInput
        placeholder="Search monitoring companies…"
        size="xs"
        value={query}
        onChange={e => search(e.target.value)}
        leftSection={<IconSearch size={11} />}
        rightSection={busy ? <span style={{ fontSize: 12, color: '#aaa' }}>…</span> : undefined}
      />
      {(results.length > 0 || (query && !busy)) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
          border: '1px solid #e2e4e8', borderRadius: 4, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
          zIndex: 200, marginTop: 2, maxHeight: 200, overflowY: 'auto',
        }}>
          {!busy && results.length === 0 && <div style={{ padding: '8px 16px', fontSize: 12, color: '#888' }}>No matching companies</div>}
          {results.map(o => (
            <div key={o.record_id} onClick={() => { onSelect(o); setQuery(''); setResults([]); }}
              style={{ padding: '8px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: 12 }}>
              <div style={{ fontWeight: 600 }}>{o.name}</div>
              <div style={{ color: '#6b7280' }}>{[o.type, o.city && o.state ? `${o.city}, ${o.state}` : '', o.phone].filter(Boolean).join(' · ')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Directory search: People ──────────────────────────────────────

function PersonSearch({ onSelect }: { onSelect: (p: PersonEntry) => void }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState<PersonEntry[]>([]);
  const [busy, setBusy]       = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = (q: string) => {
    setQuery(q);
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await queryRecords<Record<string, unknown>>(TABLES.people, {
          filters: [{ field: FP.type, operator: 'in', value: 'liaison,employee' }],
          sort: { field: FP.lastName, direction: 'asc' },
          limit: 20,
        });
        const lower = q.toLowerCase();
        setResults(res.records
          .map(r => {
            const fn = String(r[FP.firstName] ?? '');
            const ln = String(r[FP.lastName]  ?? '');
            return {
              record_id:      r.record_id as number,
              firstName:      fn,
              lastName:       ln,
              fullName:       [fn, ln].filter(Boolean).join(' '),
              email:          String(r[FP.email]          ?? ''),
              phone:          String(r[FP.phone]          ?? ''),
              title:          String(r[FP.title]          ?? ''),
              orgAffiliation: String(r[FP.orgAffiliation] ?? ''),
              primaryOrgId:   FP.primaryOrgId ? String(r[FP.primaryOrgId] ?? '') : '',
            };
          })
          .filter(p => p.fullName.toLowerCase().includes(lower) || p.email.toLowerCase().includes(lower))
        );
      } catch { /* ignore */ }
      setBusy(false);
    }, 280);
  };

  return (
    <div style={{ position: 'relative' }}>
      <TextInput
        placeholder="Search people directory by name or email…"
        size="xs"
        value={query}
        onChange={e => search(e.target.value)}
        leftSection={<IconSearch size={11} />}
        rightSection={busy ? <span style={{ fontSize: 12, color: '#aaa' }}>…</span> : undefined}
      />
      {(results.length > 0 || (query && !busy)) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
          border: '1px solid #e2e4e8', borderRadius: 4, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
          zIndex: 200, marginTop: 2, maxHeight: 200, overflowY: 'auto',
        }}>
          {!busy && results.length === 0 && <div style={{ padding: '8px 16px', fontSize: 12, color: '#888' }}>No matching people</div>}
          {results.map(p => (
            <div key={p.record_id} onClick={() => { onSelect(p); setQuery(''); setResults([]); }}
              style={{ padding: '8px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: 12 }}>
              <div style={{ fontWeight: 600 }}>{p.fullName}</div>
              <div style={{ color: '#6b7280' }}>{[p.orgAffiliation, p.email].filter(Boolean).join(' · ')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Linked card ────────────────────────────────────────────────────

function LinkedCard({ icon, title, subtitle, onClear }: {
  icon: React.ReactNode; title: string; subtitle?: string; onClear: () => void;
}) {
  return (
    <div style={{
      background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4,
      padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <div style={{ color: '#16a34a', flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#166534' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: '#374151' }}>{subtitle}</div>}
      </div>
      <button onClick={onClear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
        <IconX size={13} />
      </button>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────

export default function Monitors() {
  const { canEdit } = useModulePermission('debris');
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  const [typeFilter, setTypeFilter]   = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);

  // Modal state
  const [modalOpen, setModalOpen]     = useState(false);
  const [editTarget, setEditTarget]   = useState<Monitor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Monitor | null>(null);
  const [saving, setSaving]           = useState(false);

  // Form: type selection
  const [monitorType, setMonitorType] = useState<MonitorType>('person');

  // Linked directory entries
  const [linkedOrg, setLinkedOrg]       = useState<OrgEntry | null>(null);
  const [linkedPerson, setLinkedPerson] = useState<PersonEntry | null>(null);
  const [personOrg, setPersonOrg]       = useState<OrgEntry | null>(null); // employer of linked person
  const [loadingLink, setLoadingLink]   = useState(false);

  // Incident-specific form fields
  const [role, setRole]                   = useState('site_monitor');
  const [assignedSites, setAssignedSites] = useState('');
  const [assignedZones, setAssignedZones] = useState('');
  const [status, setStatus]               = useState('active');
  const [certifications, setCertifications] = useState('');
  const [notes, setNotes]                 = useState('');

  // ── Load ─────────────────────────────────────────────────────
  const loadMonitors = async () => {
    if (!TABLES.debrisMonitors) return;
    setLoading(true);
    try {
      // Filter by incident if the field is provisioned; otherwise show all
      const filters = FM.incidentId && incidentId
        ? [{ field: FM.incidentId, operator: 'eq' as const, value: incidentId }]
        : [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.debrisMonitors, {
        filters,
        sort: { field: FM.fullName, direction: 'asc' },
        limit: 500,
      });
      setMonitors(res.records.map(parseMonitor));
    } catch { /* table may not exist yet */ }
    setLoading(false);
  };

  useEffect(() => { loadMonitors(); }, [incidentId]);

  // ── Filter ───────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = monitors;
    if (typeFilter)   list = list.filter(m => m.monitorType === typeFilter);
    if (statusFilter) list = list.filter(m => m.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.fullName.toLowerCase().includes(q) ||
        m.company.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        m.phone.includes(q),
      );
    }
    return list;
  }, [monitors, typeFilter, statusFilter, search]);

  useEffect(() => { setPage(1); }, [filtered.length]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Group by company for display ─────────────────────────────
  const grouped = useMemo(() => {
    const companies: Monitor[] = [];
    const people: Monitor[]    = [];
    const untyped: Monitor[]   = [];
    pageRows.forEach(m => {
      if (m.monitorType === 'company') companies.push(m);
      else if (m.monitorType === 'person') people.push(m);
      else untyped.push(m);
    });
    return { companies, people, untyped };
  }, [pageRows]);

  // ── Open/close modal ─────────────────────────────────────────
  const resetModal = () => {
    setMonitorType('person');
    setLinkedOrg(null);
    setLinkedPerson(null);
    setPersonOrg(null);
    setRole('site_monitor');
    setAssignedSites('');
    setAssignedZones('');
    setStatus('active');
    setCertifications('');
    setNotes('');
  };

  const openCreate = () => {
    setEditTarget(null);
    resetModal();
    setModalOpen(true);
  };

  const openEdit = async (m: Monitor) => {
    setEditTarget(m);
    setMonitorType(m.monitorType);
    setRole(m.role);
    setAssignedSites(m.assignedSites);
    setAssignedZones(m.assignedZones);
    setStatus(m.status);
    setCertifications(m.certifications);
    setNotes(m.notes);
    setLinkedOrg(null);
    setLinkedPerson(null);
    setPersonOrg(null);
    setLoadingLink(true);
    setModalOpen(true);

    // Re-hydrate directory links
    try {
      if (m.monitorType === 'company' && m.orgId && TABLES.organizations) {
        const raw = await fetchRecord<Record<string, unknown>>(TABLES.organizations, Number(m.orgId));
        if (raw) setLinkedOrg({
          record_id: raw.record_id as number,
          name: String(raw[FO.name] ?? ''), type: String(raw[FO.type] ?? ''),
          phone: String(raw[FO.phone] ?? ''), email: String(raw[FO.email] ?? ''),
          city: String(raw[FO.city] ?? ''), state: String(raw[FO.state] ?? ''),
          licenseNumber: String(raw[FO.licenseNumber] ?? ''),
        });
      } else if (m.monitorType === 'person' && m.personId && TABLES.people) {
        const raw = await fetchRecord<Record<string, unknown>>(TABLES.people, Number(m.personId));
        if (raw) {
          const fn = String(raw[FP.firstName] ?? '');
          const ln = String(raw[FP.lastName] ?? '');
          const person: PersonEntry = {
            record_id: raw.record_id as number,
            firstName: fn, lastName: ln, fullName: [fn, ln].filter(Boolean).join(' '),
            email: String(raw[FP.email] ?? ''), phone: String(raw[FP.phone] ?? ''),
            title: String(raw[FP.title] ?? ''),
            orgAffiliation: String(raw[FP.orgAffiliation] ?? ''),
            primaryOrgId: FP.primaryOrgId ? String(raw[FP.primaryOrgId] ?? '') : '',
          };
          setLinkedPerson(person);
          if (person.primaryOrgId && TABLES.organizations) {
            const orgRaw = await fetchRecord<Record<string, unknown>>(TABLES.organizations, Number(person.primaryOrgId));
            if (orgRaw) setPersonOrg({
              record_id: orgRaw.record_id as number,
              name: String(orgRaw[FO.name] ?? ''), type: String(orgRaw[FO.type] ?? ''),
              phone: String(orgRaw[FO.phone] ?? ''), email: String(orgRaw[FO.email] ?? ''),
              city: String(orgRaw[FO.city] ?? ''), state: String(orgRaw[FO.state] ?? ''),
              licenseNumber: String(orgRaw[FO.licenseNumber] ?? ''),
            });
          }
        }
      }
    } catch { /* ignore */ }
    setLoadingLink(false);
  };

  // When a person is selected from the directory, also fetch their employer
  const selectPerson = async (p: PersonEntry) => {
    setLinkedPerson(p);
    if (p.primaryOrgId && TABLES.organizations) {
      try {
        const raw = await fetchRecord<Record<string, unknown>>(TABLES.organizations, Number(p.primaryOrgId));
        if (raw) setPersonOrg({
          record_id: raw.record_id as number,
          name: String(raw[FO.name] ?? ''), type: String(raw[FO.type] ?? ''),
          phone: String(raw[FO.phone] ?? ''), email: String(raw[FO.email] ?? ''),
          city: String(raw[FO.city] ?? ''), state: String(raw[FO.state] ?? ''),
          licenseNumber: String(raw[FO.licenseNumber] ?? ''),
        });
      } catch { /* ignore */ }
    } else {
      setPersonOrg(null);
    }
  };

  // ── Save ─────────────────────────────────────────────────────
  const handleSave = async () => {
    if (monitorType === 'company' && !linkedOrg) {
      notifications.show({ message: 'Select a monitoring company from the directory', color: '#C68700' });
      return;
    }
    if (monitorType === 'person' && !linkedPerson) {
      notifications.show({ message: 'Select an individual from the people directory', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        [FM.status]: status,
        [FM.role]:   role,
        [FM.assignedSites]: assignedSites,
        [FM.assignedZones]: assignedZones,
        [FM.certifications]: certifications,
        [FM.notes]:  notes,
      };

      if (monitorType === 'company' && linkedOrg) {
        payload[FM.fullName] = linkedOrg.name;
        payload[FM.company]  = linkedOrg.name;
        payload[FM.email]    = linkedOrg.email;
        payload[FM.phone]    = linkedOrg.phone;
        if (FM.monitorType) payload[FM.monitorType] = 'company';
        if (FM.orgId)       payload[FM.orgId]       = String(linkedOrg.record_id);
        if (FM.personId)    payload[FM.personId]    = '';
      } else if (linkedPerson) {
        payload[FM.fullName] = linkedPerson.fullName;
        payload[FM.company]  = personOrg?.name ?? linkedPerson.orgAffiliation ?? '';
        payload[FM.email]    = linkedPerson.email;
        payload[FM.phone]    = linkedPerson.phone;
        if (FM.monitorType) payload[FM.monitorType] = 'person';
        if (FM.personId)    payload[FM.personId]    = String(linkedPerson.record_id);
        if (FM.orgId)       payload[FM.orgId]       = personOrg ? String(personOrg.record_id) : '';
      }

      if (FM.incidentId && incidentId) payload[FM.incidentId] = incidentId;

      if (editTarget) {
        await updateRecord(TABLES.debrisMonitors, editTarget.id, payload);
        notifications.show({ message: 'Monitor updated', color: 'var(--color-success)' });
      } else {
        await createRecord(TABLES.debrisMonitors, payload);
        notifications.show({ message: 'Monitor added', color: 'var(--color-success)' });
      }
      await loadMonitors();
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteRecord(TABLES.debrisMonitors, deleteTarget.id);
      notifications.show({ message: `${deleteTarget.fullName} removed`, color: '#939598' });
      setDeleteTarget(null);
      await loadMonitors();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  // ── Row renderer ─────────────────────────────────────────────
  const renderRow = (m: Monitor, indent = false) => (
    <Paper
      key={m.id}
      p="sm"
      radius="md"
      withBorder
      style={{
        cursor: 'pointer',
        marginLeft: indent ? 24 : 0,
        borderLeft: indent ? '2px solid #e0e7ff' : undefined,
      }}
      onClick={() => openEdit(m)}
    >
      <Group justify="space-between">
        <Group gap={10}>
          <div style={{ color: m.monitorType === 'company' ? '#7c3aed' : '#2563eb' }}>
            {m.monitorType === 'company' ? <IconBuilding size={16} /> : <IconUser size={16} />}
          </div>
          <div>
            <Group gap={8}>
              <Text fw={600} size="sm">{m.fullName || '—'}</Text>
              <Badge size="xs" color={m.monitorType === 'company' ? 'var(--data-viz-650)' : 'var(--color-primary-700)'} variant="light">
                {m.monitorType === 'company' ? 'Company' : 'Person'}
              </Badge>
              <Badge size="xs" color={STATUS_COLOR[m.status] ?? '#939598'} variant="light">{m.status}</Badge>
              {m.role && (
                <Badge size="xs" variant="outline" color="#939598">
                  {ROLE_OPTIONS.find(r => r.value === m.role)?.label ?? m.role}
                </Badge>
              )}
              {(m.orgId || m.personId) && (
                <IconLink size={11} style={{ color: '#16a34a' }} title="Linked to directory" />
              )}
            </Group>
            <Group gap="md" mt={2}>
              {m.monitorType === 'person' && m.company && (
                <Text size="xs" c="dimmed" fw={500}>{m.company}</Text>
              )}
              {m.email && <Text size="xs" c="dimmed">{m.email}</Text>}
              {m.phone && <Text size="xs" c="dimmed">{formatPhone(m.phone)}</Text>}
            </Group>
          </div>
        </Group>
        {canEdit && (
          <Group gap={4} onClick={e => e.stopPropagation()}>
            <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(m)}>
              <IconPencil size={14} />
            </ActionIcon>
            <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(m)}>
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        )}
      </Group>
    </Paper>
  );

  // Group: companies first, then their people (matched by company name), then untyped
  const companyNames = new Set(grouped.companies.map(c => c.fullName.toLowerCase()));
  const matchedPeople = grouped.people.filter(p => p.company && companyNames.has(p.company.toLowerCase()));
  const unmatchedPeople = grouped.people.filter(p => !p.company || !companyNames.has(p.company.toLowerCase()));

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Monitors</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} loading={loading} onClick={loadMonitors}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Monitor</Button>}
        </Group>
      </Group>

      {/* Summary stats */}
      <Group gap="xs">
        <Badge size="sm" color="var(--data-viz-650)" variant="light">{monitors.filter(m => m.monitorType === 'company').length} companies</Badge>
        <Badge size="sm" color="var(--color-primary-700)"   variant="light">{monitors.filter(m => m.monitorType === 'person').length} individuals</Badge>
        <Badge size="sm" color="var(--color-success)"  variant="light">{monitors.filter(m => m.status === 'active').length} active</Badge>
      </Group>

      {/* Filters */}
      <Group gap="sm">
        <TextInput
          placeholder="Search name, company, email…"
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="xs"
          style={{ flex: 1, minWidth: 180 }}
        />
        <Select
          data={[{ value: '', label: 'All Types' }, { value: 'company', label: 'Companies' }, { value: 'person', label: 'Individuals' }]}
          value={typeFilter ?? ''}
          onChange={v => setTypeFilter(v || null)}
          size="xs"
          w={140}
        />
        <Select
          data={[{ value: '', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? ''}
          onChange={v => setStatusFilter(v || null)}
          size="xs"
          w={140}
        />
      </Group>

      {/* List — grouped by company */}
      {filtered.length === 0 ? (
        <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center' }}>
          <IconShieldCheck size={48} color="#ccc" />
          <Text c="dimmed" mt="sm">{loading ? 'Loading…' : 'No monitors found. Add one to get started.'}</Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {/* Company-type monitors with their people indented below */}
          {grouped.companies.map(company => {
            const employees = matchedPeople.filter(p => p.company.toLowerCase() === company.fullName.toLowerCase());
            return (
              <div key={company.id}>
                {renderRow(company)}
                {employees.map(p => renderRow(p, true))}
              </div>
            );
          })}
          {/* People not matched to a company in this list */}
          {unmatchedPeople.map(p => renderRow(p))}
          {/* Legacy records with no type set */}
          {grouped.untyped.map(m => renderRow(m))}
        </Stack>
      )}

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(page - 2, totalPages - 4));
            const p2 = start + i;
            return p2 <= totalPages
              ? <Button key={p2} size="xs" variant={p2 === page ? 'filled' : 'subtle'} onClick={() => setPage(p2)}>{p2}</Button>
              : null;
          })}
          <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* ── Create / Edit Modal ─────────────────────────────── */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Monitor' : 'Add Monitor'}
        centered
        size="lg"
      >
        <Stack gap="md">
          {loadingLink ? (
            <Text size="sm" c="dimmed">Loading directory link…</Text>
          ) : (
            <>
              {/* Step 1 — Type */}
              <div>
                <Text size="xs" fw={600} c="dimmed" mb={6}>Monitor Type</Text>
                <Group gap={8}>
                  <Button
                    size="xs"
                    variant={monitorType === 'person' ? 'filled' : 'light'}
                    color="var(--color-primary-700)"
                    leftSection={<IconUser size={13} />}
                    onClick={() => { setMonitorType('person'); setLinkedOrg(null); setLinkedPerson(null); setPersonOrg(null); }}
                  >
                    Individual
                  </Button>
                  <Button
                    size="xs"
                    variant={monitorType === 'company' ? 'filled' : 'light'}
                    color="var(--data-viz-650)"
                    leftSection={<IconBuilding size={13} />}
                    onClick={() => { setMonitorType('company'); setLinkedOrg(null); setLinkedPerson(null); setPersonOrg(null); }}
                  >
                    Monitoring Company
                  </Button>
                </Group>
              </div>

              {/* Step 2 — Directory link */}
              {monitorType === 'company' ? (
                <div>
                  <Text size="xs" fw={600} c="dimmed" mb={6}>Monitoring Company</Text>
                  {linkedOrg
                    ? <LinkedCard
                        icon={<IconBuilding size={16} />}
                        title={linkedOrg.name}
                        subtitle={[linkedOrg.type, linkedOrg.city && linkedOrg.state ? `${linkedOrg.city}, ${linkedOrg.state}` : '', linkedOrg.phone].filter(Boolean).join(' · ')}
                        onClear={() => setLinkedOrg(null)}
                      />
                    : <OrgSearch onSelect={setLinkedOrg} />
                  }
                </div>
              ) : (
                <div>
                  <Text size="xs" fw={600} c="dimmed" mb={6}>Individual Monitor</Text>
                  {linkedPerson
                    ? <>
                        <LinkedCard
                          icon={<IconUser size={16} />}
                          title={linkedPerson.fullName}
                          subtitle={[linkedPerson.title, linkedPerson.email].filter(Boolean).join(' · ')}
                          onClear={() => { setLinkedPerson(null); setPersonOrg(null); }}
                        />
                        {/* Their company */}
                        {personOrg ? (
                          <div style={{ marginTop: 8 }}>
                            <Text size="xs" c="dimmed" mb={4}>Monitoring Company</Text>
                            <div style={{
                              padding: '8px 16px', background: '#f5f3ff', border: '1px solid #ddd6fe',
                              borderRadius: 4, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8,
                            }}>
                              <IconBuilding size={13} style={{ color: '#7c3aed' }} />
                              <span style={{ fontWeight: 600, color: '#5b21b6' }}>{personOrg.name}</span>
                              <span style={{ color: '#6b7280', fontSize: 12 }}>via People Directory</span>
                            </div>
                          </div>
                        ) : (
                          <div style={{ marginTop: 8 }}>
                            <Text size="xs" c="dimmed" mb={4}>Monitoring Company (optional — link via People Directory)</Text>
                            <OrgSearch onSelect={org => {
                              setPersonOrg(org);
                              // Also update the person's primaryOrgId if we can
                              if (linkedPerson && FP.primaryOrgId && TABLES.people) {
                                updateRecord(TABLES.people, linkedPerson.record_id, {
                                  [FP.primaryOrgId]: String(org.record_id),
                                  [FP.orgAffiliation]: org.name,
                                }).catch(() => {});
                              }
                            }} />
                          </div>
                        )}
                      </>
                    : <PersonSearch onSelect={selectPerson} />
                  }
                </div>
              )}

              {/* Step 3 — Incident-specific fields */}
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                <Text size="xs" fw={600} c="dimmed" mb={8}>
                  {activeIncident?.name ? `${activeIncident.name} — Assignment` : 'Incident Assignment'}
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  <Select label="Role" data={ROLE_OPTIONS} value={role} onChange={v => setRole(v ?? 'site_monitor')} />
                  <Select label="Status" data={STATUS_OPTIONS} value={status} onChange={v => setStatus(v ?? 'active')} />
                  <TextInput
                    label="Certifications"
                    value={certifications}
                    onChange={e => setCertifications(e.target.value)}
                    placeholder="QSP, SWPPP, etc."
                    style={{ gridColumn: 'span 2' }}
                  />
                </SimpleGrid>
                <Textarea
                  label="Notes"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  autosize
                  minRows={2}
                  mt="sm"
                />
              </div>
            </>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Add Monitor'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Delete confirmation ─────────────────────────────── */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Remove Monitor" centered size="sm">
        <Stack>
          <Text size="sm">
            Remove <strong>{deleteTarget?.fullName}</strong> from this incident?
            {(deleteTarget?.orgId || deleteTarget?.personId) && (
              <Text span size="sm" c="dimmed"> Their directory record will not be affected.</Text>
            )}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Remove</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
